// Subscribes to the `dashboard:snapshot` Phoenix Channel. Calls
// `onChange` whenever the server pushes a `snapshot_changed` event.
//
// Auth: Phoenix.Token signed by the server and returned by /api/v1/me.
// Render's load balancer drops cookies on the WebSocket upgrade in some
// configs, so the documented Phoenix idiom of passing the token in
// connect params is the only thing that works reliably.

import { useEffect } from 'react'
import { Socket, type Channel } from 'phoenix'

let socket: Socket | null = null
let channel: Channel | null = null
let refCount = 0
let activeToken: string | null = null

function getSocket(token: string): Socket {
  if (socket && activeToken === token) return socket

  // Token rotated (or first connect) — tear down any prior socket.
  if (socket) {
    socket.disconnect()
    socket = null
    channel = null
  }

  socket = new Socket('/socket', { params: { token } })
  socket.connect()
  activeToken = token
  return socket
}

function getChannel(token: string): Channel {
  if (channel) return channel
  const s = getSocket(token)
  channel = s.channel('dashboard:snapshot', {})
  channel.join()
  return channel
}

function release() {
  refCount -= 1
  if (refCount > 0) return
  channel?.leave()
  channel = null
  socket?.disconnect()
  socket = null
  activeToken = null
}

export type SnapshotChangePayload = {
  kind: 'check_in' | 'time_off' | 'holiday' | 'parse'
  id: string
}

/**
 * `token` is the `socket_token` from /api/v1/me. Pass `null` to skip
 * connecting (the auth bootstrap hasn't resolved yet).
 */
export function useDashboardChannel(
  token: string | null,
  onChange: (payload: SnapshotChangePayload) => void
) {
  useEffect(() => {
    if (!token) return undefined

    refCount += 1
    const ch = getChannel(token)
    const ref = ch.on('snapshot_changed', onChange)

    return () => {
      ch.off('snapshot_changed', ref)
      release()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])
}
